export type OrbitHintStep<K extends string = string> = {
  key: K
  done: boolean
  text: string
}

export function nextOrbitHint<K extends string>(steps: readonly OrbitHintStep<K>[]): { key: K; text: string } | null {
  const next = steps.find((step) => !step.done)
  return next ? { key: next.key, text: next.text } : null
}

export type OrbitGuideKey = 'day' | 'pick' | 'arcana' | 'reading'

export type OrbitReadingStatus = 'ordered' | 'pending' | 'in_progress' | 'clarifying' | 'ready' | 'answered' | 'closing' | 'closed' | 'rejected'

export const ORBIT_READING_LABEL: Record<OrbitReadingStatus, string> = {
  ordered: 'Ждёт рассказа',
  pending: 'Ждёт Людмилу',
  in_progress: 'Людмила записывает',
  clarifying: 'Сообщение у Людмилы',
  ready: 'Ответ готов',
  answered: 'Ответ получен',
  closing: 'Завершаю разбор',
  closed: 'Разбор завершён',
  rejected: 'Лунары вернулись',
}

export type OrbitReadingPlanetState = OrbitReadingStatus | 'none'

export const ORBIT_READING_ICON: Record<
  OrbitReadingPlanetState,
  'orders' | 'hourglass' | 'pen' | 'question' | 'mail' | 'check' | 'undo'
> = {
  none: 'orders',
  ordered: 'question',
  pending: 'hourglass',
  in_progress: 'pen',
  clarifying: 'question',
  ready: 'mail',
  answered: 'mail',
  closing: 'check',
  closed: 'check',
  rejected: 'undo',
}

export const ORBIT_READING_PILL: Partial<Record<OrbitReadingPlanetState, string>> = {
  ready: '1 новый ответ',
  ordered: 'ждёт вашего рассказа',
  closing: 'ждёт вашего ответа',
}

export function orbitReadingPill(state: OrbitReadingPlanetState, unheard: number): string | null {
  if (state === 'ready' && unheard > 1) return `${unheard} ${newAnswers(unheard)}`
  return ORBIT_READING_PILL[state] ?? null
}

function newAnswers(n: number): string {
  const tail = n % 100
  if (tail >= 11 && tail <= 14) return 'новых ответов'
  if (n % 10 === 1) return 'новый ответ'
  if (n % 10 >= 2 && n % 10 <= 4) return 'новых ответа'
  return 'новых ответов'
}

export function orbitReadingPlanetState(reading: { status: OrbitReadingStatus } | null | undefined): OrbitReadingPlanetState {
  return reading?.status ?? 'none'
}

export type ReadingTabMarker =
  | { kind: 'wait'; icon: 'hourglass' | 'pen' | 'question'; label: string }
  | { kind: 'ready'; count: number; label: string }
  | { kind: 'closing'; label: string }

export function readingTabMarker(reading: { status: OrbitReadingStatus; unheard: number } | null | undefined): ReadingTabMarker | null {
  if (!reading) return null
  switch (reading.status) {
    case 'ordered':
      return { kind: 'closing', label: 'разбор ждёт вашего рассказа' }
    case 'pending':
      return { kind: 'wait', icon: 'hourglass', label: 'ждёт Людмилу' }
    case 'in_progress':
      return { kind: 'wait', icon: 'pen', label: 'Людмила записывает ответ' }
    case 'clarifying':
      return { kind: 'wait', icon: 'question', label: 'сообщение у Людмилы' }
    case 'ready': {
      const count = Math.max(1, reading.unheard)
      return { kind: 'ready', count, label: `${count} ${newAnswers(count)}` }
    }
    case 'closing':
      return { kind: 'closing', label: 'Людмила завершает разбор' }
    default:
      return null
  }
}

const READING_HINT: Partial<Record<OrbitReadingStatus, string>> = {
  ordered: 'Расскажите Людмиле, что волнует',
  ready: 'Людмила ответила, послушайте',
  closing: 'Людмила завершает разбор',
}

type GuideInput = {
  guest: boolean
  dayAvailable: boolean
  dayOpened: boolean
  pickOpen: boolean
  picked: boolean
  arcana: boolean
  reading: OrbitReadingStatus | null
}

export function orbitGuide(input: GuideInput): { key: OrbitGuideKey; text: string } | null {
  const day = { key: 'day' as const, done: !input.dayAvailable || input.dayOpened, text: 'Начните отсюда: карта уже ждёт' }
  if (input.guest) {
    return nextOrbitHint([day, { key: 'pick', done: false, text: 'Войдите, чтобы продолжить' }])
  }
  const required = nextOrbitHint<OrbitGuideKey>([
    day,
    { key: 'pick', done: input.picked || !input.pickOpen, text: 'Теперь доверьтесь интуиции' },
    { key: 'arcana', done: input.arcana, text: 'Узнайте свой аркан' },
  ])
  if (required) return required
  const lit = input.dayOpened && input.picked && input.arcana
  if (!lit) return null
  if (!input.reading) return { key: 'reading', text: 'Закажите разбор у Людмилы' }
  const text = READING_HINT[input.reading]
  return text ? { key: 'reading', text } : null
}
